export default `---
slug: copilot-connectors
title: "Connecting the Enterprise to Copilot"
year: 2026
category: work
featured: false
tags: [Microsoft Copilot, Microsoft 365, Knowledge Management, Project Management, Confluence, ServiceNow, Jira, AEM, Enterprise AI]
thumbnail: /images/projects/copilot_connectors.jpg
description: "Bringing the company's real knowledge into Copilot — Confluence, ServiceNow, Jira, AEM. The roadmap is set, the first connector lands mid-November 2026."
relatedLinks:
  - label: "AI Knowledge Readiness Framework"
    href: /projects/ai-knowledge-readiness-framework
  - label: "Swift — Enterprise AI"
    href: /career/swift-enterprise-ai
---

**This project is still running.** The first connector goes live mid-November 2026. More to follow.

---

![Connecting the Enterprise to Copilot](/images/projects/copilot_connectors.jpg)

SharePoint connects to Microsoft 365 Copilot by default. Which is nice, but SharePoint is not where the real knowledge lives.

The real knowledge lives in Confluence — thousands of internal documentation pages, runbooks, and how-tos. In **Adobe Experience Manager**, where the customer-facing product documentation is published. In **ServiceNow**, where every IT service ticket, incident, and change record sits. In **Jira**, where projects and backlogs are tracked. All of that content exists; none of it was reachable by Copilot.

The project is about closing that gap.

## The Roadmap

My role covers the full strategic and delivery lifecycle: identifying the knowledge sources worth connecting, scoping and prioritising them by business value, managing stakeholder alignment across product owners, IT, and governance, and running each connector project end-to-end from scoping through testing and rollout.

The approach is deliberate rather than opportunistic. Not "let's connect everything and see what happens" — but a structured assessment of which sources would generate the most value for the most users, in what order, and what the requirements are for each before Copilot can consume them reliably. Spoiler: the [AI Knowledge Readiness Framework](#/projects/ai-knowledge-readiness-framework) comes in handy here.

## Where It Stands

The **Confluence connector** is the first one out. Go-live is planned for mid-November 2026, followed by a wave rollout targeting over a thousand users.

AEM, ServiceNow, and Jira are scoped and sequenced on the roadmap. Each has its own timeline and complexity — AEM because it's the customer-facing layer (higher scrutiny), ServiceNow and Jira because the knowledge structures there require more preparation work before they retrieve well.

## Why This Matters

The average person at a large company spends a non-trivial fraction of their day looking for information that already exists somewhere — in a ticket, a doc page, a process guide. Copilot with proper connectivity doesn't replace that knowledge; it makes it findable without the scavenger hunt.

Getting there requires more than a technical integration. It requires that the knowledge itself is ready to be retrieved accurately — which is exactly what the preceding framework work was building toward.

The connectors are the delivery mechanism. The knowledge quality work was the prerequisite.
`;
